import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import TopCommentBlock from "../components/TopCommentBlock";
import ClosingCountdown from "../components/ClosingCountdown";

const FLAG_URL =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/c/cf/Flag_of_Canada.svg/1280px-Flag_of_Canada.svg.png";

export default function Home() {
  const { user } = useAuth();
  const { t } = useI18n();

  const ctaTo = !user ? "/register" : user.has_paid ? "/dashboard" : "/paywall";
  const ctaText = !user
    ? t("home_cta_signup")
    : user.has_paid
    ? t("home_cta_vote")
    : t("home_cta_unlock");

  return (
    <div className="min-h-screen bg-[#F4F4F4] text-black" data-testid="home-page">
      <section className="max-w-6xl mx-auto px-5 sm:px-10 pt-10 sm:pt-16 pb-8">
        <div className="border-2 border-black brutal-shadow overflow-hidden bg-white" data-testid="home-flag">
          <img
            src={FLAG_URL}
            alt="Canadian flag"
            className="w-full h-56 sm:h-72 md:h-96 object-contain bg-white p-4"
          />
        </div>
        <div className="mt-3 flex items-center justify-between font-mono-civic text-[10px] sm:text-xs uppercase tracking-[0.3em] text-black">
          <span>{t("home_flag_caption")}</span>
          <span>{t("home_flag_scroll")}</span>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-10 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-7">
            <h1 className="font-serif-display font-extrabold text-black leading-[0.9] tracking-tighter text-5xl sm:text-6xl md:text-7xl">
              {t("home_h1_a")}<br />{t("home_h1_b")} <span className="italic">{t("home_h1_emph")}</span>.
            </h1>

            <p className="mt-8 font-bold text-black text-lg sm:text-xl leading-relaxed max-w-prose" data-testid="home-manifesto">
              {t("home_manifesto")}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to={ctaTo}
                className="inline-flex items-center justify-center bg-black text-white font-bold uppercase tracking-widest px-8 py-5 border-2 border-black brutal-shadow hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
                data-testid="home-cta-btn"
              >
                {ctaText} →
              </Link>
              {user?.has_paid && (
                <Link
                  to="/dashboard"
                  className="inline-flex items-center justify-center bg-white text-black font-bold uppercase tracking-widest px-8 py-5 border-2 border-black hover:bg-black hover:text-white transition-colors"
                  data-testid="home-dashboard-btn"
                >
                  {t("home_btn_dashboard")}
                </Link>
              )}
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="bg-white border-2 border-black brutal-shadow-red p-6 sm:p-8" data-testid="home-main-question">
              <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-[#E00000] mb-4">
                {t("home_headline_q_tag")}
              </div>
              <p className="font-serif-display italic text-black text-3xl sm:text-4xl leading-[1.05]" style={{ fontWeight: 800 }}>
                {t("home_headline_q")}
              </p>
              <div className="mt-6 h-[2px] w-full bg-black" />
              <div className="mt-6 font-mono-civic text-xs uppercase tracking-widest text-black" style={{ fontWeight: 800 }}>
                {t("home_headline_q_note")}
              </div>
            </div>
          </div>
        </div>

        <ClosingCountdown />

        <TopCommentBlock />
      </section>

      <section className="border-t-2 border-black bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-10 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div data-testid="home-feature-1">
            <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black">01</div>
            <h3 className="mt-2 font-serif-display font-extrabold text-2xl text-black">{t("home_feature1_h")}</h3>
            <p className="mt-2 font-bold text-black">{t("home_feature1_p")}</p>
          </div>
          <div data-testid="home-feature-2">
            <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black">02</div>
            <h3 className="mt-2 font-serif-display font-extrabold text-2xl text-black">{t("home_feature2_h")}</h3>
            <p className="mt-2 font-bold text-black">{t("home_feature2_p")}</p>
          </div>
          <div data-testid="home-feature-3">
            <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black">03</div>
            <h3 className="mt-2 font-serif-display font-extrabold text-2xl text-black">{t("home_feature3_h")}</h3>
            <p className="mt-2 font-bold text-black">{t("home_feature3_p")}</p>
          </div>
        </div>
      </section>

      <footer className="border-t-2 border-black">
        <div className="max-w-6xl mx-auto px-5 sm:px-10 py-6 flex items-center justify-between font-mono-civic text-xs uppercase tracking-widest text-black flex-wrap gap-2">
          <span>{t("home_footer_left")}</span>
          <Link to="/legal" className="underline" data-testid="home-legal-link">{t("splash_legal_link")}</Link>
        </div>
      </footer>
    </div>
  );
}
